import { longDate } from "@/components/landing/SlotPicker";
import { useLocation, Link } from "react-router-dom";
import { MessageCircle, Mail, Clock, ArrowUpRight } from "lucide-react";
import { BlogLayout } from "@/pages/blog/BlogLayout";
import { useSeo } from "@/pages/blog/useSeo";
import { MaskLine, Reveal } from "@/components/landing/Reveal";
import { WHATSAPP_URL } from "@/components/landing/data";
import { VisitSteps } from "./VisitSteps";
import { PocketGauge } from "./PocketGauge";

const BRING = ["Eventuali radiografie o esami recenti della bocca", "L'elenco dei farmaci che assumi", "Le domande che ti vengono in mente: ci sarà tempo per tutte"];

const Hero = ({ done }) => (
  <section className="max-w-6xl mx-auto px-6 sm:px-10 pt-36 sm:pt-44 pb-16" data-testid="first-visit-hero">
    <p className="eyebrow text-[#3E6B48]">{done ? "Richiesta ricevuta" : "La prima visita"}</p>
    <h1 className="f-serif text-5xl sm:text-7xl text-[#1B3B28] mt-5 leading-[0.98] tracking-[-0.01em]" data-testid="first-visit-title">
      <MaskLine>{done ? `Grazie, ${done.name}.` : "La prima visita,"}</MaskLine>
      <MaskLine delay={0.12}><em className="text-[#3E6B48]">{done ? "Ecco cosa ti aspetta." : "passo dopo passo."}</em></MaskLine>
    </h1>
    <Reveal delay={0.3}>
      <p className="text-lg text-[#526356] mt-8 max-w-2xl leading-relaxed">
        {done ? (done.slot ? `Hai scelto ${longDate(done.slot.date)} alle ${done.slot.time}: lo staff conferma l'orario e ti arriva un'email. ` : "Ti ricontatteremo al più presto, nella fascia oraria che hai indicato, per fissare insieme il giorno della visita. ") : ""}
        Qui trovi come si svolge il primo incontro, così puoi arrivare sapendo esattamente cosa succederà. Niente sorprese.
      </p>
      <div className="flex flex-wrap gap-3 mt-8">
        <span className="inline-flex items-center gap-2 rounded-full bg-[#E6ECE4] px-4 py-2 text-sm text-[#1B3B28]"><Clock size={15} /> Durata: circa 45–60 minuti</span>
        {done?.emailSent && <span className="inline-flex items-center gap-2 rounded-full bg-[#E6ECE4] px-4 py-2 text-sm text-[#1B3B28]" data-testid="first-visit-email-note"><Mail size={15} /> Conferma inviata a {done.email}</span>}
      </div>
    </Reveal>
  </section>
);

export default function FirstVisitPage() {
  const done = useLocation().state;
  useSeo({ title: "La prima visita parodontale: come si svolge", description: "Accoglienza, anamnesi, sondaggio parodontale iniziale e valutazione delle tasche: come si svolge la prima visita per la parodontite dal Dott. Nicola Mauro.", path: "/prima-visita" });
  return (
    <BlogLayout>
      <div data-testid="first-visit-page">
        <Hero done={done} />
        <section className="max-w-6xl mx-auto px-6 sm:px-10 py-16 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4 lg:sticky lg:top-32 self-start">
            <p className="eyebrow text-[#3E6B48]">Come si svolge</p>
            <h2 className="f-serif text-4xl sm:text-5xl text-[#1B3B28] mt-4 leading-tight">Cinque momenti, <em className="text-[#3E6B48]">al tuo ritmo.</em></h2>
          </div>
          <div className="lg:col-span-8"><VisitSteps /></div>
        </section>
        <section className="bg-[#E6ECE4] py-24 mt-8">
          <div className="max-w-6xl mx-auto px-6 sm:px-10">
            <p className="eyebrow text-[#3E6B48]">Il sondaggio in parole semplici</p>
            <h2 className="f-serif text-4xl sm:text-5xl text-[#1B3B28] mt-4 max-w-3xl leading-tight">Cosa ci dicono i <em className="text-[#3E6B48]">millimetri</em> delle tue tasche</h2>
            <p className="text-[#526356] mt-5 max-w-2xl leading-relaxed">La sonda misura quanto è profondo lo spazio tra dente e gengiva. Sposta il cursore per vedere cosa significa ogni misura.</p>
            <div className="mt-12"><PocketGauge /></div>
          </div>
        </section>
        <section className="max-w-6xl mx-auto px-6 sm:px-10 py-24 grid md:grid-cols-2 gap-12" data-testid="first-visit-bring">
          <div>
            <p className="eyebrow text-[#3E6B48]">Se vuoi, porta con te</p>
            <ul className="mt-6 divide-y divide-[#D5DFD3] border-y border-[#D5DFD3]">
              {BRING.map((b) => <li key={b} className="py-4 text-[#1E2922]">{b}</li>)}
            </ul>
          </div>
          <div className="rounded-[2rem] bg-[#1B3B28] text-[#FCFBF7] p-8 sm:p-10">
            <p className="f-serif text-3xl leading-tight">Hai una domanda prima della visita?</p>
            <p className="text-[#FCFBF7]/70 mt-3">Scrivici quando vuoi: rispondiamo con calma, senza impegno.</p>
            <div className="flex flex-wrap gap-3 mt-8">
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#86C89B] text-[#0F2317] px-5 py-3 text-sm" data-testid="first-visit-whatsapp"><MessageCircle size={16} /> Scrivici su WhatsApp</a>
              <Link to="/blog" className="inline-flex items-center gap-2 rounded-full border border-[#FCFBF7]/30 px-5 py-3 text-sm hover:bg-[#FCFBF7]/10" data-testid="first-visit-blog-link">Leggi il blog <ArrowUpRight size={15} /></Link>
              <Link to="/" className="inline-flex items-center gap-2 rounded-full border border-[#FCFBF7]/30 px-5 py-3 text-sm hover:bg-[#FCFBF7]/10" data-testid="first-visit-home-link">Torna al sito</Link>
            </div>
          </div>
        </section>
      </div>
    </BlogLayout>
  );
}
